<template>
    <div class="online-planner-employee" data-testid="online-planner-employee">
        <p class="online-planner-settings__type-notice online-planner-employee__intro">
            <span class="online-planner-settings__type-notice-icon" aria-hidden="true">
                <frm-icon name="info-i" />
            </span>
            <span v-html="$t('settings.external-interfaces.online-planner.employee.intro')" />
        </p>

        <div v-if="loading" class="online-planner-employees__loading">
            <frm-spinner />
        </div>

        <div v-else-if="loadFailed" class="online-planner-employees__empty" data-testid="online-planner-employee-error">
            {{ $t('settings.external-interfaces.online-planner.employees.load-error') }}
            <button class="button button--text" type="button" @click="fetchEmployee">
                {{ $t('settings.external-interfaces.online-planner.employees.retry') }}
            </button>
        </div>

        <template v-else>
            <section class="online-planner-employee__section" :class="{'online-planner-employee__section--divided': advanced}">
                <h3 class="online-planner-employee__heading">
                    {{ $t('settings.external-interfaces.online-planner.employee.availability') }}
                </h3>

                <div class="online-planner-employee__card">
                    <div class="online-planner-employee__row">
                        <div>
                            <strong class="online-planner-employee__title">
                                {{ $t('settings.external-interfaces.online-planner.employee.available-title') }}
                            </strong>
                            <p class="online-planner-employee__description">
                                {{ $t('settings.external-interfaces.online-planner.employee.available-description') }}
                            </p>
                        </div>
                        <span
                            class="online-planner-employees__availability"
                            :class="{'online-planner-employees__availability--online': employee.online_daypart_count}"
                            data-testid="online-planner-employee-availability"
                        >
                            {{ employee.online_daypart_count
                                ? $t('settings.external-interfaces.online-planner.overview.available')
                                : $t('settings.external-interfaces.online-planner.overview.unavailable') }}
                        </span>
                    </div>

                    <div class="online-planner-employee__schedules">
                        <div class="online-planner-employee__row online-planner-employee__row--centered">
                            <div>
                                <strong class="online-planner-employee__title">
                                    {{ $t('settings.external-interfaces.online-planner.employee.schedules') }}
                                </strong>
                                <p class="online-planner-employee__summary">
                                    <template v-if="employee.schedule_count">
                                        {{ $tc('settings.external-interfaces.online-planner.employee.schedule-count', employee.schedule_count, {count: employee.schedule_count}) }}
                                        ·
                                        {{ $t('settings.external-interfaces.online-planner.employees.online-count', {
                                            online: employee.online_daypart_count,
                                            total: employee.daypart_count,
                                        }) }}
                                    </template>
                                    <template v-else>
                                        {{ $t('settings.external-interfaces.online-planner.employees.no-schedules') }}
                                    </template>
                                </p>
                            </div>
                            <button
                                v-if="employee.schedule_count"
                                class="button online-planner-emails__action online-planner-employee__action"
                                type="button"
                                data-testid="online-planner-employee-edit-schedules"
                                @click="openSchedules"
                            >
                                {{ $t('settings.external-interfaces.online-planner.overview.edit-schedules') }}
                            </button>
                        </div>
                        <div v-if="employee.online_dayparts.length" class="online-planner-employees__overview-slots online-planner-employee__chips">
                            <span v-for="dayPart in employee.online_dayparts" :key="dayPart.id">
                                <strong>{{ weekday(dayPart.day_of_the_week) }}</strong>
                                {{ formatTime(dayPart.start) }}–{{ formatTime(dayPart.end) }}
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            <section v-if="advanced" class="online-planner-employee__section">
                <header class="online-planner-employee__row online-planner-employee__row--centered">
                    <h3 class="online-planner-employee__heading">
                        {{ $t('settings.external-interfaces.online-planner.employee.routing-title') }}
                    </h3>
                    <button
                        class="online-planner-employees__configuration-link online-planner-employee__configure"
                        type="button"
                        data-testid="online-planner-employee-configure"
                        @click="openConfiguration"
                    >
                        {{ $t('settings.external-interfaces.online-planner.employees.configuration.configure') }}
                    </button>
                </header>
                <p class="online-planner-employee__description">
                    {{ $t('settings.external-interfaces.online-planner.employee.routing-description') }}
                </p>

                <p v-if="employeeMissesRouting" class="online-planner-employees__overview-not-findable online-planner-employee__not-findable" data-testid="online-planner-employee-not-findable">
                    <span class="online-planner-employees__schedule-dot" />
                    {{ $t('settings.external-interfaces.online-planner.overview.not-findable') }}
                </p>

                <div class="online-planner-employee__card online-planner-employee__card--routing">
                    <div class="online-planner-employee__group">
                        <span class="online-planner-employee__label">
                            {{ $t('settings.external-interfaces.online-planner.employee.specializations') }}
                        </span>
                        <div v-if="specializations.length" class="online-planner-employees__overview-chips online-planner-employee__chips">
                            <span v-for="specialization in specializations" :key="specialization.key">{{ specialization.label }}</span>
                        </div>
                        <span v-else class="online-planner-employee__summary">
                            {{ $t('settings.external-interfaces.online-planner.employees.configuration.not-in-use') }}
                        </span>
                    </div>

                    <div class="online-planner-employee__group">
                        <span class="online-planner-employee__label">
                            {{ $t('settings.external-interfaces.online-planner.employees.configuration.complaint-areas') }}
                        </span>
                        <div v-if="selectedComplaintAreas.length" class="online-planner-employees__overview-chips online-planner-employee__chips">
                            <span v-for="complaintArea in selectedComplaintAreas" :key="complaintArea.id">{{ complaintArea.name }}</span>
                        </div>
                        <span v-else class="online-planner-employee__summary online-planner-employee__summary--missing">
                            {{ $t('settings.external-interfaces.online-planner.employees.configuration.no-complaint-areas') }}
                        </span>
                    </div>
                </div>
            </section>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import FormatsDayParts from '../../mixins/FormatsDayParts';
import ModalManager from '../../services/modalManager';
import EmployeeConfiguration from './EmployeeConfiguration.vue';
import EmployeeSchedules from './EmployeeSchedules.vue';
import { enabledSpecializations, missesRouting } from './employeeRouting';

export default {
    mixins: [FormatsDayParts],

    props: {
        employeeUrl: {
            type: String,
            required: true,
        },

        employeeConfigurationUrl: {
            type: String,
            required: true,
        },

        advanced: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            employee: null,
            loading: true,
            loadFailed: false,
        };
    },

    computed: {
        selectedComplaintAreas() {
            return (this.employee.complaint_area_ids || [])
                .map(id => (this.employee.complaint_areas || []).find(complaintArea => complaintArea.id === id))
                .filter(Boolean);
        },

        specializations() {
            return enabledSpecializations(this.employee);
        },

        employeeMissesRouting() {
            return missesRouting(this.employee);
        },
    },

    mounted() {
        this.fetchEmployee();
    },

    methods: {
        async fetchEmployee() {
            this.loading = true;
            this.loadFailed = false;

            try {
                const [employee, configuration] = await Promise.all([
                    axios.get(this.employeeUrl),
                    axios.get(this.employeeConfigurationUrl),
                ]);

                this.employee = {
                    ...employee.data.data,
                    ...configuration.data.data,
                    complaint_areas: configuration.data.complaint_areas,
                };
            } catch (error) {
                this.loadFailed = true;
            } finally {
                this.loading = false;
            }
        },

        async openSchedules() {
            const updatedEmployee = await ModalManager.openComponentInModal(EmployeeSchedules, {
                employee: this.employee,
                url: this.employeeUrl,
            }, {}, 'online-planner-schedules');

            if (updatedEmployee) {
                this.employee = { ...this.employee, ...updatedEmployee };
            }
        },

        async openConfiguration() {
            const configuration = await ModalManager.openComponentInModal(EmployeeConfiguration, {
                employee: this.employee,
                url: this.employeeConfigurationUrl,
            }, {
                'configuration-refreshed': this.updateConfiguration,
            }, 'online-planner-employee-configuration');

            if (!configuration) {
                return;
            }

            this.updateConfiguration(configuration);
            this.$emit('configuration-updated', this.employee);
        },

        updateConfiguration({ complaint_areas: complaintAreas, ...configuration }) {
            this.employee = {
                ...this.employee,
                ...configuration,
                complaint_areas: complaintAreas || this.employee.complaint_areas,
            };
        },
    },
};
</script>
